import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  extractActorId,
  fetchSessions,
  fetchSessionEvents,
  SessionSummary,
  SessionEvent,
} from './api';
import {
  ChatMessage,
  HistorySidebarProps,
  mapEventsToChatMessages,
  formatSessionDate,
  getSessionDisplayName,
  sortSessionsByDate,
} from './historySidebar.utils';

// ─── Component ───────────────────────────────────────────────────────────────

export type { ChatMessage, HistorySidebarProps } from './historySidebar.utils';

export function HistorySidebar({
  isOpen,
  onClose,
  onSelectSession,
  currentSessionId,
  domainIdentifier,
  disabled,
}: HistorySidebarProps) {
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nextToken, setNextToken] = useState<string | undefined>(undefined);
  const [historyExpanded, setHistoryExpanded] = useState(true);

  // Compute actorId once for the component lifecycle
  const actorId = useMemo(() => {
    try {
      return extractActorId();
    } catch (err: any) {
      console.warn('[HistorySidebar] Failed to extract actorId:', err.message);
      return null;
    }
  }, []);

  const sortedSessions = useMemo(() => sortSessionsByDate(sessions), [sessions]);

  // Close sidebar on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const fetchMemorySessions = useCallback(async (token?: string) => {
    setLoading(true);
    setError(null);
    if (!token) setNextToken(undefined);

    try {
      if (!actorId) {
        throw new Error('Unable to determine user identity');
      }

      const result = await fetchSessions(actorId, domainIdentifier, token);

      setSessions(prev => token ? [...prev, ...result.sessions] : result.sessions);
      setNextToken(result.nextToken);
    } catch (err: any) {
      setError(err.message || 'Failed to load sessions');
    } finally {
      setLoading(false);
    }
  }, [actorId, domainIdentifier]);

  // Load sessions when sidebar opens
  useEffect(() => {
    if (isOpen) {
      fetchMemorySessions();
    }
  }, [isOpen, fetchMemorySessions]);

  const handleSelectSession = useCallback(async (session: SessionSummary) => {
    setLoadingEvents(true);
    setError(null);

    try {
      if (!actorId) {
        throw new Error('Unable to determine user identity');
      }

      let allEvents: SessionEvent[] = [];
      let token: string | undefined;
      do {
        const result = await fetchSessionEvents(session.sessionId, actorId, domainIdentifier, token);
        allEvents = allEvents.concat(result.events);
        token = result.nextToken;
      } while (token);

      const messages = mapEventsToChatMessages(allEvents, session.sessionId);
      onSelectSession(session.sessionId, messages);
    } catch (err: any) {
      setError(err.message || 'Failed to load conversation');
    } finally {
      setLoadingEvents(false);
    }
  }, [actorId, domainIdentifier, onSelectSession]);

  return (
    <div
      className={`rv-sidebar glass glass-strong ${isOpen ? 'rv-sidebar--open' : ''}`}
      aria-hidden={!isOpen}
      role="complementary"
      aria-label="Conversation history"
    >
      <div className="rv-sidebar__section">
        <button
          className="rv-section-toggle"
          onClick={() => setHistoryExpanded(prev => !prev)}
          aria-expanded={historyExpanded}
          aria-controls="rv-history-list"
        >
          <span className={`rv-section-arrow ${historyExpanded ? 'rv-arrow-down' : ''}`}>›</span>
          Recent conversations
        </button>

        {historyExpanded && (
          <div id="rv-history-list">
            {error && <div className="rv-sidebar-error" role="alert">{error}</div>}

            {loading && sessions.length === 0 && (
              <div className="rv-sidebar-loading" aria-live="polite">Loading sessions…</div>
            )}

            {!loading && sessions.length === 0 && !error && (
              <div className="rv-sidebar-empty">No past conversations</div>
            )}

            <div className="rv-session-list" role="listbox" aria-label="Session list">
              {sortedSessions.map(session => (
                <button
                  key={session.sessionId}
                  className={`rv-session-item ${session.sessionId === currentSessionId ? 'rv-session-active' : ''}`}
                  onClick={() => handleSelectSession(session)}
                  disabled={loadingEvents || (disabled && session.sessionId !== currentSessionId)}
                  role="option"
                  aria-selected={session.sessionId === currentSessionId}
                >
                  <span className="rv-session-date">
                    {formatSessionDate(session.updatedAt || session.createdAt)}
                  </span>
                  <span className="rv-session-id">
                    {getSessionDisplayName(session.sessionId)}
                  </span>
                </button>
              ))}
            </div>

            {nextToken && !loading && (
              <button
                className="rv-load-more-btn"
                onClick={() => fetchMemorySessions(nextToken)}
                disabled={loading}
              >
                Load more
              </button>
            )}

            {loadingEvents && (
              <div className="rv-sidebar-loading" aria-live="polite">Loading conversation…</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
